import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from "lucide-react";
import NavBar from "../components/NavBar";
import { getSampleReport } from "../lib/api";
import { PaidReport } from "../components/Reports";
import { PhotoOutcomeHero, ExecutiveSummary, WeddingLookGallery } from "./AnalysisResult";

/**
 * Public, zero-AI-cost "Explore a Sample Vowze Report" page.
 * Emergent Update Patch (Oct 2026) — replaces the old free-AI-report CTA.
 * Fetches ONE pre-curated document (GET /api/sample-report) and reuses the
 * exact same report components as a real paid report — no AI calls, no
 * report-generation jobs, no database writes on view.
 */
export default function SampleReport() {
    const [sample, setSample] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        getSampleReport()
            .then(setSample)
            .catch(() => setError("Sample report isn't configured yet — please check back soon."));
    }, []);

    if (error) {
        return (
            <div className="min-h-screen bg-ivory" data-testid="sample-report-error">
                <NavBar />
                <div className="mx-auto max-w-2xl px-6 pt-40 text-center">
                    <p className="text-ink">{error}</p>
                    <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-charcoal underline">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back home
                    </Link>
                </div>
            </div>
        );
    }

    if (!sample) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-ivory" data-testid="sample-report-loading">
                <Loader2 className="h-6 w-6 animate-spin text-gold" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ivory" data-testid="sample-report-page">
            <NavBar />
            <section className="pt-32 pb-24 md:pt-40">
                <div className="mx-auto max-w-6xl px-6 md:px-12">
                    <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink/70 hover:text-charcoal" data-testid="back-home-from-sample">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back
                    </Link>

                    <div
                        className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold/15 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal"
                        data-testid="sample-report-badge"
                    >
                        <Sparkles className="h-3.5 w-3.5 text-gold" /> Sample Report — Illustrative Demo
                    </div>

                    <div className="mt-5 max-w-2xl">
                        <h1 className="font-serif text-4xl leading-tight text-charcoal md:text-5xl" data-testid="sample-report-title">
                            Explore a Sample Vowze Report
                        </h1>
                        <p className="mt-4 text-[16px] text-ink">
                            See how Vowze transforms personal colour analysis, coordinated wedding styling and
                            event-specific visualizations into a personalized wedding vision. The names and photos
                            below are illustrative — your own report is built entirely from your own photos and answers.
                        </p>
                    </div>

                    <div className="mt-10">
                        <WeddingLookGallery analysis={sample} />
                    </div>

                    <div className="mt-10 space-y-8">
                        <PhotoOutcomeHero analysis={sample} paidReport={sample.paid_report} />
                        <ExecutiveSummary analysis={sample} paidReport={sample.paid_report} />
                        <PaidReport report={sample.paid_report} gallery={sample.gallery || {}} />
                    </div>

                    <div className="mt-14 rounded-[28px] bg-charcoal p-8 text-center text-ivory shadow-soft md:p-12" data-testid="sample-report-cta">
                        <h2 className="font-serif text-2xl md:text-3xl">Ready to see your own?</h2>
                        <p className="mx-auto mt-3 max-w-xl text-[15px] text-ivory/75">
                            Choose your event pack, upload your photos, and get your own fully personalised styling strategy — coordinated to your venue, your colours, and your faces.
                        </p>
                        <Link
                            to="/#pricing"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-charcoal transition hover:bg-gold/90"
                            data-testid="sample-report-start-cta"
                        >
                            Choose Your Event Pack <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
